import {
  type ArcEntity,
  type CircleEntity,
  type DimensionEntity,
  type DimensionKind,
  type EllipseEntity,
  type Entity,
  type EntityStyle,
  type HatchEntity,
  type LineEntity,
  type PolylineEntity,
  type SplineEntity,
  type TextAnchor,
  type TextEntity,
  type TextVerticalAlign,
} from './types';
import { Vec2, normalizeAngle, arcSweep } from './math';
import { nextId } from './ids';

export const DEFAULT_TEXT_HEIGHT = 3.5;
export const DEFAULT_TEXT_STYLE = 1;

export function makeLine(
  a: Vec2,
  b: Vec2,
  layerId: string,
  style: EntityStyle = {},
): LineEntity {
  return { id: nextId('l'), type: 'line', layerId, a, b, ...style };
}

export function makePolyline(
  vertices: Vec2[],
  closed: boolean,
  layerId: string,
  style: EntityStyle = {},
): PolylineEntity {
  return {
    id: nextId('p'),
    type: 'polyline',
    layerId,
    vertices: dedupeVertices(vertices),
    closed,
    ...style,
  };
}

export function makeCircle(
  center: Vec2,
  radius: number,
  layerId: string,
  style: EntityStyle = {},
): CircleEntity {
  return { id: nextId('c'), type: 'circle', layerId, center, radius: Math.abs(radius), ...style };
}

export function makeArc(
  center: Vec2,
  radius: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
  layerId: string,
  style: EntityStyle = {},
): ArcEntity {
  return {
    id: nextId('a'),
    type: 'arc',
    layerId,
    center,
    radius: Math.abs(radius),
    startAngle: normalizeAngle(startAngle),
    endAngle: normalizeAngle(endAngle),
    ccw,
    ...style,
  };
}

export function makeEllipse(
  center: Vec2,
  rx: number,
  ry: number,
  rotation: number,
  layerId: string,
  style: EntityStyle = {},
): EllipseEntity {
  return {
    id: nextId('el'),
    type: 'ellipse',
    layerId,
    center,
    rx: Math.abs(rx),
    ry: Math.abs(ry),
    rotation,
    ...style,
  };
}

export interface SplineInit {
  degree?: number;
  closed?: boolean;
  weights?: number[];
}

/** Clamped rational B-spline through (or near) its control points. */
export function makeSpline(
  controlPoints: Vec2[],
  layerId: string,
  init: SplineInit = {},
  style: EntityStyle = {},
): SplineEntity {
  const spline: SplineEntity = {
    id: nextId('sp'),
    type: 'spline',
    layerId,
    controlPoints: controlPoints.map((point) => point.clone()),
    degree: init.degree ?? 3,
    closed: init.closed ?? false,
    ...(init.weights && init.weights.length === controlPoints.length
      ? { weights: [...init.weights] }
      : {}),
    ...style,
  };
  return spline;
}

export interface TextInit {
  height?: number;  rotation?: number;
  anchor?: TextAnchor;
  valign?: TextVerticalAlign;
  widthFactor?: number;
  style?: EntityStyle;
}

export function makeText(
  position: Vec2,
  text: string,
  layerId: string,
  init: TextInit = {},
): TextEntity {
  return {
    id: nextId('t'),
    type: 'text',
    layerId,
    position,
    text,
    height: init.height ?? DEFAULT_TEXT_HEIGHT,
    rotation: init.rotation ?? 0,
    anchor: init.anchor ?? 'left',
    valign: init.valign ?? 'baseline',
    widthFactor: init.widthFactor ?? 1,
    ...(init.style ?? {}),
  };
}

export interface HatchInit {
  angle?: number;
  scale?: number;
  origin?: Vec2;
  style?: EntityStyle;
}

export function makeHatch(
  loops: Vec2[][],
  pattern: string,
  layerId: string,
  init: HatchInit = {},
): HatchEntity {
  return {
    id: nextId('h'),
    type: 'hatch',
    layerId,
    loops,
    pattern,
    angle: init.angle ?? 0,
    scale: init.scale ?? 1,
    origin: init.origin ?? Vec2.of(0, 0),
    ...(init.style ?? {}),
  };
}

export function makeRectangle(
  a: Vec2,
  b: Vec2,
  layerId: string,
  style: EntityStyle = {},
): PolylineEntity {
  return makePolyline(
    [
      Vec2.of(a.x, a.y),
      Vec2.of(b.x, a.y),
      Vec2.of(b.x, b.y),
      Vec2.of(a.x, b.y),
    ],
    true,
    layerId,
    style,
  );
}

export const DEFAULT_DIMENSION_TEXT_HEIGHT = 3.5;
export const DEFAULT_DIMENSION_ARROW = 2.5;

export interface DimensionInit {
  kind?: DimensionKind;
  textHeight?: number;
  arrowSize?: number;
  precision?: number;
  textOverride?: string;
  prefix?: string;
  suffix?: string;
}

export function makeDimension(
  p1: Vec2,
  p2: Vec2,
  offset: number,
  layerId: string,
  init: DimensionInit = {},
): DimensionEntity {
  return {
    id: nextId('d'),
    type: 'dimension',
    layerId,
    kind: init.kind ?? 'linear',
    p1,
    p2,
    offset,
    textHeight: init.textHeight ?? DEFAULT_DIMENSION_TEXT_HEIGHT,
    arrowSize: init.arrowSize ?? DEFAULT_DIMENSION_ARROW,
    precision: init.precision ?? 2,
    ...(init.textOverride !== undefined ? { textOverride: init.textOverride } : {}),
    ...(init.prefix !== undefined ? { prefix: init.prefix } : {}),
    ...(init.suffix !== undefined ? { suffix: init.suffix } : {}),
  };
}

/** Cheap clone with a fresh id; entity geometry is treated as immutable. */
export function withNewId<T extends Entity>(entity: T): T {
  return { ...entity, id: nextId(entity.type.slice(0, 2)) };
}

function dedupeVertices(vertices: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const v of vertices) {
    const last = out[out.length - 1];
    if (!last || !last.equals(v, 1e-9)) out.push(v);
  }
  return out;
}

/** Circular arc through three points, or `null` when they are collinear. */
export function arcThroughPoints(
  p1: Vec2,
  p2: Vec2,
  p3: Vec2,
  layerId: string,
  style: EntityStyle = {},
): ArcEntity | null {
  const d = 2 * (p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y));
  if (Math.abs(d) < 1e-12) return null;

  const p1Sq = p1.x * p1.x + p1.y * p1.y;
  const p2Sq = p2.x * p2.x + p2.y * p2.y;
  const p3Sq = p3.x * p3.x + p3.y * p3.y;

  const cx = (p1Sq * (p2.y - p3.y) + p2Sq * (p3.y - p1.y) + p3Sq * (p1.y - p2.y)) / d;
  const cy = (p1Sq * (p3.x - p2.x) + p2Sq * (p1.x - p3.x) + p3Sq * (p2.x - p1.x)) / d;
  const center = Vec2.of(cx, cy);
  const radius = center.distanceTo(p1);

  const startAngle = p1.sub(center).angle();
  const endAngle = p3.sub(center).angle();
  const midAngle = p2.sub(center).angle();
  const ccw = arcSweep(startAngle, endAngle, true) >= arcSweep(startAngle, midAngle, true);
  return makeArc(center, radius, startAngle, endAngle, ccw, layerId, style);
}

/** Boundary loop of a closed entity, used for hatching. */
export function entityLoop(entity: Entity): Vec2[] | null {
  switch (entity.type) {
    case 'circle':
      return circleToLoop(entity);
    case 'ellipse':
      return ellipseToLoop(entity);
    case 'polyline':
      return entity.closed ? entity.vertices.map((v) => v.clone()) : null;
    default:
      return null;
  }
}

function circleToLoop(circle: CircleEntity, segments = 128): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < segments; i++) {
    out.push(
      Vec2.of(
        circle.center.x + Math.cos((i / segments) * Math.PI * 2) * circle.radius,
        circle.center.y + Math.sin((i / segments) * Math.PI * 2) * circle.radius,
      ),
    );
  }
  return out;
}

function ellipseToLoop(ellipse: EllipseEntity, segments = 128): Vec2[] {
  const out: Vec2[] = [];
  const c = Math.cos(ellipse.rotation);
  const s = Math.sin(ellipse.rotation);
  for (let i = 0; i < segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    const x = Math.cos(t) * ellipse.rx;
    const y = Math.sin(t) * ellipse.ry;
    out.push(Vec2.of(ellipse.center.x + x * c - y * s, ellipse.center.y + x * s + y * c));
  }
  return out;
}
